import React, { useEffect, useState } from 'react';
import { Box, Button, Typography, Modal } from '@mui/material';
import { tentActions } from '../../redux-utils/actions/tent-actions';
import { useDispatch, useSelector } from 'react-redux';
import { useAuth } from '../../custom-hooks/useAuth';
import withReactContent from 'sweetalert2-react-content';
import Swal from 'sweetalert2';
import Loader from '../Loader';
import { encryptSingleFormData } from '../../utils/crypt-module/encrypt';
import { useTranslation } from 'react-i18next';

function EditTentModal({ handleClose, open, style, selectedTent }) {
    const dispatch = useDispatch();
    const { t } = useTranslation();

    const ReactSwal = withReactContent(Swal);
    const { status, error } = useSelector((state) => state.tentState);
    const [formInputs, setFormInputs] = useState({
        tent_name: '',
        type: '',
        capacity: '',
    });
    const [tentParamsToUpdate, setTentParamsToUpdate] = useState(null);
    const { updateTentAction, resetTentAction, updateTentLocally } =
        tentActions;
    const [errors, setErrors] = useState({});
    const [staffErrors, setStaffErrors] = useState([]);
    const { getLogin } = useAuth();
    const loginStorage = JSON.parse(getLogin());
    const [staffs, setStaffs] = useState([
        {
            first_name: '',
            last_name: '',
            role: '',
        },
    ]);

    const addStaffHandler = () => {
        setStaffs((prev) => {
            return [
                ...prev,
                {
                    first_name: '',
                    last_name: '',
                    role: '',
                },
            ];
        });
    };

    const removeStaffHandler = (index) => {
        const updated_staffs = staffs.filter((_, i) => i !== index);
        setStaffs(updated_staffs);
    };

    const handleInputChange = (event) => {
        const { name, value } = event.target;

        setFormInputs((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleStaffInputChange = (index, field, value) => {
        const updated_staffs = [...staffs];
        updated_staffs[index][field] = value;
        setStaffs(updated_staffs);
    };

    const validateForm = () => {
        const newErrors = {};
        const newStaffErrors = [];

        if (!formInputs.tent_name.trim())
            newErrors.tent_name = 'Tent name is required.';
        if (!formInputs.type) newErrors.type = 'type is required.';
        if (!formInputs.capacity || formInputs.capacity <= 0)
            newErrors.capacity = 'Capacity must be greater than 0.';

        staffs.forEach((staff, index) => {
            const staffError = {};
            if (!staff.first_name)
                staffError.first_name = 'First name is required.';
            if (!staff.last_name)
                staffError.last_name = 'Last name is required.';
            if (!staff.role) staffError.role = 'Role is required.';
            newStaffErrors.push(staffError);
        });

        setErrors(newErrors);
        setStaffErrors(newStaffErrors);

        return (
            Object.keys(newErrors).length === 0 &&
            newStaffErrors.every((e) => Object.keys(e).length === 0)
        );
    };

    const handleFormSubmit = (event) => {
        event.preventDefault();

        if (validateForm()) {
            const loginData = loginStorage?.loginData?.data;
            const publicKey = process.env.REACT_APP_PUB_KEY_1;

            if (!publicKey) {
                console.error(
                    'Public key is missing. Please check your environment configuration.'
                );
                return;
            }

            const params = {
                id: selectedTent.id,
                token: loginData?.token,
                formData: {
                    name: formInputs.tent_name,
                    capacity: formInputs.capacity,
                    type: formInputs.type,
                    staffs: [...staffs],
                },
            };

            const has_supervisor = params.formData.staffs.some(
                (staff) => staff.role === 'supervisor'
            );

            const has_assistant = params?.formData?.staffs.some(
                (staff) => staff.role === 'assistant'
            );

            if (!has_supervisor) {
                ReactSwal.fire({
                    title: t('tent-management-page.Error!'),
                    text: t(
                        'tent-management-page.Kindly add at least one supervisor to proceed'
                    ),
                    icon: 'error',
                });
                return;
            }

            if (!has_assistant) {
                ReactSwal.fire({
                    title: t('tent-management-page.Error!'),
                    text: t(
                        'tent-management-page.Kindly add at least one assistant to proceed'
                    ),
                    icon: 'error',
                });
                return;
            }

            encryptSingleFormData({
                publicKey: publicKey,
                formData: params.formData,
            })
                .then((encryptedData) => {
                    const data = {
                        encryptedFormData: encryptedData,
                        id: params.id,
                        token: params.token,
                    };

                    dispatch(updateTentAction(data));
                    setTentParamsToUpdate(params.formData);
                })
                .catch((error) => {
                    console.error('Encryption failed:', error);
                    alert('Failed to encrypt the form data. Please try again.');
                });
        } else {
            console.warn('Form validation failed.');
        }
    };

    useEffect(() => {
        if (status === 'tent_update_success') {
            ReactSwal.fire({
                title: t('tent-management-page.Success!'),
                text: t('tent-management-page.Tent updated successfully'),
                icon: 'success',
            }).then(() => {
                handleClose();
                dispatch(resetTentAction());
                if (tentParamsToUpdate) {
                    dispatch(updateTentLocally(tentParamsToUpdate));
                }
            });
        }

        if (status === 'tent_update_failed') {
            const errorMessage =
                error?.message || t('tent-management-page.Tent update failed');

            ReactSwal.fire({
                title: 'Error!',
                text: errorMessage,
                icon: 'error',
                confirmButtonText: 'OK',
            }).then(() => {
                dispatch(resetTentAction());
            });
        }
    }, [
        status,
        handleClose,
        ReactSwal,
        error,
        dispatch,
        resetTentAction,
        tentParamsToUpdate,
        updateTentLocally,
        t,
    ]);

    useEffect(() => {
        if (selectedTent) {
            setFormInputs({
                tent_name: selectedTent.name || '',
                type: selectedTent.type || '',
                capacity: selectedTent.capacity || '',
            });

            if (selectedTent.staffs && selectedTent.staffs.length > 0) {
                setStaffs(selectedTent.staffs);
            } else {
                setStaffs([
                    {
                        first_name: '',
                        last_name: '',
                        role: '',
                    },
                ]);
            }
        }
    }, [selectedTent]);
    return (
        <>
            <Modal
                aria-labelledby="modal-modal-title"
                aria-describedby="modal-modal-description"
                className="-z-50 relative"
                open={open}
                onClose={handleClose}
            >
                <Box style={style}>
                    {status === 'tent_update_start' && <Loader />}
                    <form onSubmit={handleFormSubmit}>
                        <div className="w-full h-[85vh] rounded-lg bg-white p-10 pt-0 overflow-auto relative">
                            <div className="flex justify-between items-center w-auto bg-white h-20 sticky top-0">
                                <Typography
                                    variant="h4"
                                    sx={{
                                        color: '#2F4858',
                                        fontWeight: '500',
                                        fontSize: '28px',
                                    }}
                                >
                                    Update Tent
                                </Typography>
                                <div className="flex gap-5 items-center">
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        sx={{
                                            backgroundColor: '#FDB315',
                                            px: 3,
                                            py: '0.59rem',
                                            borderRadius: '999px',
                                            color: 'white',
                                            fontWeight: 600,
                                            fontSize: '12px',
                                            '&:hover': {
                                                backgroundColor: '#FDB315',
                                                color: 'white',
                                            },
                                        }}
                                    >
                                        {t('tent-management-page.Update')}
                                    </Button>
                                    <div
                                        onClick={handleClose}
                                        className="cursor-pointer bg-[#EFEFEF] hover:bg-[#eeeeff] w-10 h-10 flex items-center justify-center rounded-full"
                                    >
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="16"
                                            height="16"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            stroke-width="2"
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                            class="size-4"
                                        >
                                            <path
                                                stroke="none"
                                                d="M0 0h24v24H0z"
                                                fill="none"
                                            />
                                            <path d="M18 6l-12 12" />
                                            <path d="M6 6l12 12" />
                                        </svg>
                                    </div>
                                </div>
                            </div>
                            <div className="mt-5">
                                <div className="border rounded-lg p-5">
                                    <div className="flex flex-col w-[100%]">
                                        <label className="font-medium text-[14px]">
                                            {t(
                                                'tent-management-page.Tent Name'
                                            )}
                                        </label>
                                        <input
                                            type="text"
                                            onChange={handleInputChange}
                                            name="tent_name"
                                            value={formInputs.tent_name}
                                            className="border px-4 mt-1 py-2 w-full text-gray-500 rounded-lg outline-[#FDB315] "
                                        />
                                        <div>
                                            <span className="text-red-500 text-xs">
                                                {errors.tent_name}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="flex flex-row mt-3 gap-5">
                                        <div className="w-[50%]">
                                            <label className="font-medium text-[14px]">
                                                {t('tent-management-page.Type')}
                                            </label>
                                            <select
                                                onChange={handleInputChange}
                                                name="type"
                                                value={formInputs.type}
                                                className="border px-4 mt-1 py-2 w-full text-gray-500 rounded-lg outline-[#FDB315] "
                                            >
                                                <option value="">
                                                    - SELECT-
                                                </option>
                                                <option value="male">
                                                    {t(
                                                        'tent-management-page.Male'
                                                    )}
                                                </option>
                                                <option value="female">
                                                    {t(
                                                        'tent-management-page.Female'
                                                    )}
                                                </option>
                                            </select>
                                            <div>
                                                <span className="text-red-500 text-xs">
                                                    {errors.type}
                                                </span>
                                            </div>
                                        </div>

                                        <div className="w-[50%]">
                                            <label className="font-medium text-[14px]">
                                                {t(
                                                    'tent-management-page.Capacity'
                                                )}
                                            </label>
                                            <input
                                                onChange={handleInputChange}
                                                type="number"
                                                name="capacity"
                                                value={formInputs.capacity}
                                                className="border px-4 mt-1 py-2 w-full text-gray-500 rounded-lg outline-[#FDB315] "
                                            />
                                            <div>
                                                <span className="text-red-500 text-xs">
                                                    {errors.capacity}
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="text-[14px] font-semibold  text-gray-700 mb-3 mt-3">
                                        {t('tent-management-page.Staffs')}
                                    </div>
                                    <hr />
                                    {staffs.map((staff, index) => (
                                        <div
                                            key={index}
                                            className="border rounded-lg p-4 mt-3"
                                        >
                                            <div className="flex flex-row gap-5 mt-3">
                                                <div className="w-[50%]">
                                                    <label className="font-medium text-[14px]">
                                                        {t(
                                                            'tent-management-page.First Name'
                                                        )}
                                                    </label>
                                                    <input
                                                        onChange={(e) =>
                                                            handleStaffInputChange(
                                                                index,
                                                                'first_name',
                                                                e.target.value
                                                            )
                                                        }
                                                        type="text"
                                                        value={staff.first_name}
                                                        className="border px-4 mt-1 py-2 w-full text-gray-500 rounded-lg outline-[#FDB315]"
                                                        placeholder="First Name"
                                                    />
                                                    <div>
                                                        <span className="text-red-500 text-xs">
                                                            {
                                                                staffErrors[
                                                                    index
                                                                ]?.first_name
                                                            }
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className="w-[50%]">
                                                    <label className="font-medium text-[14px]">
                                                        {t(
                                                            'tent-management-page.Last Name'
                                                        )}
                                                    </label>
                                                    <input
                                                        onChange={(e) =>
                                                            handleStaffInputChange(
                                                                index,
                                                                'last_name',
                                                                e.target.value
                                                            )
                                                        }
                                                        type="text"
                                                        value={staff.last_name}
                                                        className="border px-4 mt-1 py-2 w-full text-gray-500 rounded-lg outline-[#FDB315] "
                                                        placeholder="Last Name"
                                                    />
                                                    <div>
                                                        <span className="text-red-500 text-xs">
                                                            {
                                                                staffErrors[
                                                                    index
                                                                ]?.last_name
                                                            }
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="flex flex-row mt-3">
                                                <div className="w-[50%]">
                                                    <label className="font-medium text-[14px]">
                                                        {t(
                                                            'tent-management-page.Role'
                                                        )}
                                                    </label>
                                                    <select
                                                        onChange={(e) =>
                                                            handleStaffInputChange(
                                                                index,
                                                                'role',
                                                                e.target.value
                                                            )
                                                        }
                                                        value={staff.role}
                                                        className=" border px-4 mt-1 py-2 w-full text-gray-500 rounded-lg outline-[#FDB315]"
                                                    >
                                                        <option value="">
                                                            - SELECT -
                                                        </option>
                                                        <option value="supervisor">
                                                            {t(
                                                                'tent-management-page.Supervisor'
                                                            )}
                                                        </option>
                                                        <option value="assistant">
                                                            {t(
                                                                'tent-management-page.Assistant'
                                                            )}
                                                        </option>
                                                    </select>
                                                    <div>
                                                        <span className="text-red-500 text-xs">
                                                            {
                                                                staffErrors[
                                                                    index
                                                                ]?.role
                                                            }
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                            {index > 0 && (
                                                <button
                                                    onClick={() =>
                                                        removeStaffHandler(
                                                            index
                                                        )
                                                    }
                                                    type="button"
                                                    className="bg-[red] mt-3 text-white font-medium text-sm py-1.5 px-3 rounded hover:bg-[#e2a300] transition"
                                                >
                                                    X &nbsp;
                                                    {t(
                                                        'tent-management-page.Remove'
                                                    )}
                                                </button>
                                            )}
                                        </div>
                                    ))}

                                    <button
                                        type="button"
                                        onClick={addStaffHandler}
                                        className="bg-[#FDB315] mt-3 text-white font-medium text-sm py-1.5 px-3 rounded hover:bg-[#e2a300] transition"
                                    >
                                        + &nbsp;
                                        {t('tent-management-page.Add Staff')}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </form>
                </Box>
            </Modal>
        </>
    );
}

export default EditTentModal;
